import renderSite from './pages/site.js';
import { PAGES } from './lib/pages.js';
import { applyMeta, setRobots } from './lib/seo.js';

// The admin area, the sign-in screens and the Auth/Functions SDKs they need
// are loaded on demand: a visitor reading the public site never downloads
// them.
const lazy = {
  auth: () => import('./lib/auth.js'),
  login: () => import('./pages/login.js'),
  admin: () => import('./pages/admin.js'),
  setup: () => import('./pages/setup.js'),
};

const root = document.getElementById('app');
const boot = document.getElementById('boot');

// A prerendered page (see vite-seo.js) arrives with its content already in
// the root. The first render then loads live content quietly behind it -- no
// progress bar, no spinner -- and keeps that DOM if nothing has changed.
let prerendered = root.firstElementChild;

// Returned by the previous page when it holds a timer or listener that must
// stop before the DOM is replaced.
let teardown = null;

// Every render takes a ticket. A render that is overtaken while awaiting must
// not go on to touch the DOM: each one clears the root early and appends
// later, so two in flight would otherwise both append and the page would show
// two of everything.
let renderSeq = 0;

// Changes the address bar without re-rendering. The admin uses it when it has
// already drawn the new view itself, so opening a record does not tear down
// and rebuild the whole console.
export function setPath(to, { replace = false } = {}) {
  if (to === location.pathname) return;
  if (replace) history.replaceState({}, '', to);
  else history.pushState({}, '', to);
}

export function navigate(to, { replace = false } = {}) {
  if (replace) history.replaceState({}, '', to);
  else history.pushState({}, '', to);
  render();
}

// Shown while a route resolves. Navigation clears the old page before the new
// one's content has loaded, which otherwise leaves the viewport blank.
function routeSpinner() {
  const node = document.createElement('div');
  node.className = 'route-load';
  node.innerHTML =
    '<div class="boot-mark"><img src="/assets/logo-mark.png" alt="" /></div>' +
    '<div class="boot-rail"><i></i></div>';
  return node;
}

function topBar() {
  const node = document.createElement('div');
  node.className = 'route-bar';
  node.innerHTML = '<i></i>';
  return node;
}

function hideBoot() {
  if (!boot || boot.classList.contains('done')) return;
  boot.classList.add('done');
  // The class hides it outright; the timer only tidies the node away. Removal
  // must not depend on a transition or animation event, because
  // prefers-reduced-motion disables both and the overlay would then sit over
  // the whole site forever.
  setTimeout(() => boot.remove(), 600);
}

function resolve(pathname) {
  if (pathname === '/login') return { kind: 'login' };
  if (pathname === '/admin/setup') return { kind: 'setup' };
  if (pathname === '/admin' || pathname.startsWith('/admin/')) {
    // /admin/<section>/<record> — so a refresh, a bookmark or a shared link
    // lands where it says, instead of bouncing to the dashboard.
    const [, , section = 'dashboard', ...rest] = pathname.split('/');
    return { kind: 'admin', section, recordId: rest.join('/') || null };
  }

  const eventMatch = pathname.match(/^\/events\/([\w-]+)$/);
  if (eventMatch) return { kind: 'site', page: 'events', eventId: eventMatch[1] };

  // A trailing slash is the same page; anything else unknown renders home
  // but is flagged so it is not indexed as a copy of it.
  const clean = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  const page = PAGES.find((p) => p.path === clean);
  return { kind: 'site', page: page ? page.id : 'home', eventId: null, notFound: !page };
}

async function render() {
  const seq = ++renderSeq;
  const superseded = () => seq !== renderSeq;

  const route = resolve(location.pathname);
  const hydrate = prerendered;
  prerendered = null;
  if (hydrate) route.hydrate = hydrate;
  const isAdminArea = route.kind === 'admin' || route.kind === 'setup';
  // The sign-in screen is not admin chrome, but it is no search result either.
  setRobots(isAdminArea || route.kind === 'login' ? 'noindex, nofollow, noarchive' : '');

  // The top bar starts immediately (it covers the auth check too); the
  // centred spinner only appears if the route is genuinely slow, so a fast
  // navigation does not flash it.
  const bar = boot?.isConnected || hydrate ? null : topBar();
  if (bar) document.body.appendChild(bar);

  let spinner = null;
  // Only once the route is genuinely slow does the outgoing page give way to
  // the spinner; before that it stays put rather than flashing blank.
  const spinnerTimer = setTimeout(() => {
    if (superseded() || hydrate) return;
    spinner = routeSpinner();
    root.replaceChildren(spinner);
  }, 140);

  const done = () => {
    clearTimeout(spinnerTimer);
    spinner?.remove();
    bar?.remove();
    hideBoot();
  };

  const fail = (heading, detail) => {
    root.replaceChildren(
      Object.assign(document.createElement('div'), {
        className: 'inner pad',
        innerHTML: `<h1 class="page-title">${heading}</h1><p class="page-lead">${detail}</p><a href="/">Back to the site</a>`,
      }),
    );
  };

  try {
    if (route.kind === 'admin') {
      const { waitForAuth, getRole } = await lazy.auth();
      if (superseded()) return;
      const user = await waitForAuth();
      if (superseded()) return;
      if (!user) {
        navigate(`/login?next=${encodeURIComponent(location.pathname)}`, { replace: true });
        return;
      }
      // A role granted after this session signed in is not in the cached ID
      // token, so a stale token must not be the reason someone is locked out:
      // re-check once against a freshly refreshed token before refusing.
      let role = await getRole(user);
      if (role !== 'admin') role = await getRole(user, true);
      if (superseded()) return;
      if (role !== 'admin') {
        teardown?.();
        teardown = null;
        fail('Not authorised', 'This account is not an administrator.');
        return;
      }
    }

    const page =
      route.kind === 'login'
        ? (await lazy.login()).default
        : route.kind === 'setup'
          ? (await lazy.setup()).default
          : route.kind === 'admin'
            ? (await lazy.admin()).default
            : renderSite;
    if (superseded()) return;

    // Built off-document and swapped in once finished. Page renderers await
    // their data before appending, so writing straight into the root lets two
    // overlapping renders each append and the page shows two of everything.
    const view = document.createElement('div');
    const pageTeardown = await page(view, route);

    if (superseded()) {
      pageTeardown?.();
      return;
    }

    teardown?.();
    teardown = pageTeardown ?? null;
    document.body.classList.toggle('admin-body', isAdminArea);
    if (!view.adopted) root.replaceChildren(...view.childNodes);
    if (view.seo) applyMeta(view.seo);
  } catch (error) {
    console.error(error);
    fail('Something went wrong', 'Please reload the page.');
  } finally {
    done();
  }
}

document.addEventListener('click', (event) => {
  const link = event.target.closest('a[href^="/"]');
  if (!link || link.target || event.metaKey || event.ctrlKey || event.shiftKey) return;
  event.preventDefault();
  const href = link.getAttribute('href');
  if (href !== location.pathname) window.scrollTo(0, 0);
  navigate(href);
});

window.addEventListener('popstate', render);

render();
